import React, { useState } from 'react';
import {
  View, Text, TouchableOpacity, Modal, FlatList,
  StyleSheet, Pressable, Image, Alert,
} from 'react-native';
import { Feather } from '@expo/vector-icons';
import { useBusiness } from '../contexts/BusinessContext';
import { useAppTheme } from '../contexts/ThemeContext';
import { useAuth } from '../hooks/useAuth';
import { useSubscription } from '../hooks/useSubscription';

export default function BusinessSwitcher({ onAddNew }) {
  const { activeBusiness, businesses, switchBusiness } = useBusiness();
  const { colors, spacing, typography, radius } = useAppTheme();
  const { user } = useAuth();
  const { isPro } = useSubscription(user?.id);

  function handleAddNew() {
    if (!isPro) {
      Alert.alert(
        'Pro Feature',
        'Managing multiple businesses is a Pro feature. Upgrade to add and switch between businesses.',
        [
          { text: 'Not Now', style: 'cancel' },
          { text: 'Upgrade to Pro', onPress: () => { setOpen(false); onAddNew?.('upgrade'); } },
        ],
      );
      return;
    }
    setOpen(false);
    onAddNew?.();
  }
  const [open, setOpen] = useState(false);

  if (!activeBusiness) return null;

  return (
    <>
      {/* Trigger button */}
      <TouchableOpacity
        onPress={() => setOpen(true)}
        style={[styles.pill, {
          backgroundColor: colors.surface,
          borderColor: colors.border,
          borderRadius: radius.full,
        }]}
        activeOpacity={0.7}
      >
        <View style={styles.pillTextWrap}>
          <Text style={[styles.pillName, { color: colors.text }]} numberOfLines={1}>
            {activeBusiness.name}
          </Text>
          {activeBusiness.trade_type ? (
            <Text style={[styles.pillSub, { color: colors.textMuted }]} numberOfLines={1}>
              {activeBusiness.trade_type}
            </Text>
          ) : null}
        </View>
        <Feather name="chevron-down" size={14} color={colors.textMuted} />
      </TouchableOpacity>

      {/* Mid-screen modal */}
      <Modal visible={open} transparent animationType="fade" onRequestClose={() => setOpen(false)}>
        <Pressable style={styles.backdrop} onPress={() => setOpen(false)}>
          <Pressable style={[styles.card, {
            backgroundColor: colors.surfaceElevated,
            borderColor: colors.border,
            borderRadius: radius.xl,
          }]} onPress={() => {}}>

            <View style={[styles.cardHeader, { borderBottomColor: colors.border }]}>
              <Text style={[styles.cardTitle, { color: colors.text }]}>Switch Business</Text>
              <TouchableOpacity onPress={() => setOpen(false)} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
                <Feather name="x" size={18} color={colors.textMuted} />
              </TouchableOpacity>
            </View>

            <FlatList
              data={businesses}
              keyExtractor={b => b.id}
              scrollEnabled={businesses.length > 4}
              renderItem={({ item }) => {
                const isActive = activeBusiness.id === item.id;
                return (
                  <TouchableOpacity
                    style={[styles.row, isActive && {
                      backgroundColor: colors.accentLight,
                    }]}
                    onPress={() => { switchBusiness(item); setOpen(false); }}
                    activeOpacity={0.7}
                  >
                    {item.logo_url ? (
                      <Image source={{ uri: item.logo_url }} style={styles.rowLogo} />
                    ) : (
                      <View style={[styles.rowAvatar, {
                        backgroundColor: isActive ? colors.accent : colors.surface,
                        borderColor: colors.border,
                      }]}>
                        <Text style={[styles.rowAvatarText, {
                          color: isActive ? '#fff' : colors.text,
                        }]}>
                          {item.name.charAt(0).toUpperCase()}
                        </Text>
                      </View>
                    )}
                    <View style={styles.rowInfo}>
                      <Text style={[styles.rowName, { color: colors.text }]}>{item.name}</Text>
                      <Text style={[styles.rowSub, { color: colors.textMuted }]}>
                        {[item.trade_type, item.business_type].filter(Boolean).join(' · ') || 'No type set'}
                      </Text>
                    </View>
                    {isActive && (
                      <View style={[styles.activeBadge, { backgroundColor: colors.accent }]}>
                        <Feather name="check" size={12} color="#fff" />
                      </View>
                    )}
                  </TouchableOpacity>
                );
              }}
              ItemSeparatorComponent={() => (
                <View style={[styles.sep, { backgroundColor: colors.border }]} />
              )}
            />

            <View style={[styles.sep, { backgroundColor: colors.border }]} />
            <TouchableOpacity
              style={styles.addRow}
              onPress={handleAddNew}
              activeOpacity={0.7}
            >
              <View style={[styles.addIcon, { backgroundColor: isPro ? colors.accentLight : colors.border, borderRadius: radius.sm }]}>
                <Feather name={isPro ? 'plus' : 'lock'} size={16} color={isPro ? colors.accent : colors.textMuted} />
              </View>
              <Text style={[styles.addText, { color: isPro ? colors.accent : colors.textMuted }]}>Add New Business</Text>
              {!isPro && (
                <View style={{ backgroundColor: colors.border, borderRadius: 4, paddingHorizontal: 5, paddingVertical: 1, marginLeft: 'auto' }}>
                  <Text style={{ fontSize: 9, fontWeight: '800', color: colors.textMuted, letterSpacing: 0.5 }}>PRO</Text>
                </View>
              )}
            </TouchableOpacity>

          </Pressable>
        </Pressable>
      </Modal>
    </>
  );
}

const styles = StyleSheet.create({
  pill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderWidth: 1,
    maxWidth: 220,
    alignSelf: 'flex-start',
  },
  pillLogo: { width: 22, height: 22, borderRadius: 4 },
  pillAvatar: {
    width: 22, height: 22, borderRadius: 5,
    alignItems: 'center', justifyContent: 'center',
  },
  pillAvatarText: { fontSize: 11, fontWeight: '700' },
  pillTextWrap: { flex: 1, minWidth: 0 },
  pillName: { fontSize: 13, fontWeight: '600' },
  pillSub: { fontSize: 10, marginTop: 1 },
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.6)',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 24,
  },
  card: {
    width: '100%',
    borderWidth: 1,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 16 },
    shadowOpacity: 0.4,
    shadowRadius: 32,
    elevation: 20,
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
  },
  cardTitle: { fontSize: 16, fontWeight: '700', letterSpacing: -0.3 },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 14,
    gap: 14,
  },
  rowLogo: { width: 40, height: 40, borderRadius: 8 },
  rowAvatar: {
    width: 40, height: 40, borderRadius: 10,
    alignItems: 'center', justifyContent: 'center',
    borderWidth: 1,
  },
  rowAvatarText: { fontSize: 17, fontWeight: '700' },
  rowInfo: { flex: 1 },
  rowName: { fontSize: 15, fontWeight: '600' },
  rowSub: { fontSize: 12, marginTop: 2 },
  activeBadge: {
    width: 22, height: 22, borderRadius: 11,
    alignItems: 'center', justifyContent: 'center',
  },
  sep: { height: 1, marginHorizontal: 20 },
  addRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 14,
    gap: 14,
  },
  addIcon: {
    width: 40, height: 40,
    alignItems: 'center', justifyContent: 'center',
  },
  addText: { fontSize: 15, fontWeight: '600' },
});
